// Keeping the target URL when an in-scope link opens the INSTALLED app.
//
// The manifest is scope "/", so the OS hands any porter.innoshape.com.au link to
// the installed PWA. If the app is already running, a launch may only FOCUS that
// window — the operator stays on whatever screen they were last on and the link
// is silently dropped. That is how an assistant's OAuth consent went missing:
// /oauth/consent?authorization_id=… became the calendar, and the authorization
// sat pending for ever. launch_handler asks for navigate-existing; this reads
// launchQueue, which carries the target URL even when nothing navigated.

import { useEffect } from 'react'
import { useNavigate } from 'react-router'

interface LaunchParams { targetURL?: string }
interface LaunchQueue { setConsumer: (consumer: (params: LaunchParams) => void) => void }

export function useLaunchQueue(): void {
  const navigate = useNavigate()

  useEffect(() => {
    const queue = (window as Window & { launchQueue?: LaunchQueue }).launchQueue
    if (!queue) return // Not Chromium, or not installed — the browser navigated normally.

    queue.setConsumer((params) => {
      if (!params.targetURL) return
      let target: URL
      try { target = new URL(params.targetURL, window.location.href) } catch { return }
      // Same origin only: launchQueue is fed by the OS, so an off-origin target
      // would make this an open redirect driven from outside the app.
      if (target.origin !== window.location.origin) return

      const here = window.location.pathname + window.location.search
      const there = target.pathname + target.search
      // A plain launch targets start_url; navigating to where we already are
      // would only remount the screen the operator is looking at.
      if (there === here || there === '/') return
      navigate(there, { replace: true })
    })
  }, [navigate])
}
